import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef, NO_ERRORS_SCHEMA, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Firestore, collection, query, where, getDocs, doc, setDoc, getDoc } from '@angular/fire/firestore';
import { Storage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from '@angular/fire/storage';
import { Auth } from '@angular/fire/auth';
import { HttpClientModule } from '@angular/common/http';
import { combineLatest, Subscription, firstValueFrom } from 'rxjs';
import * as XLSX from 'xlsx';

import { PdfGeneratorService } from '../services/pdf-generator.service';
import { AccessService } from '../services/access.service';
import { CompanyService } from '../services/company.service';
import { StoreService } from '../services/store.service';
import { InvoiceService } from '../services/invoice.service';
import { ExchangeRateService } from '../services/exchange-rate.service';
import { FeeRateService, SmrsRate } from '../services/fee-rate.service';
import { SalesInvoiceService } from '../services/sales-invoice.service';
import { EmailFormComponent } from '../email-form/email-form.component';
import { BrandService } from '../services/brand.service';
import { Brand } from '../models/brand.model';
import { PhotoboothService } from '../services/photobooth.service';
import { SalesReportModalComponent } from './sales-report-modal.component';
import { deviceBelongsToBrand } from '../services/device-brand.util';

export interface MonthlyDeviceDetail {
  storeId: string;
  storeName: string;
  deviceId: string;
  deviceName: string;
  paper: number;
  card: { sales: number; overPayment: number; sum: number };
  cash: { sales: number; overPayment: number; sum: number };
  forced: { sales: number; overPayment: string; sum: string };
  coupon: { sales: number; overPayment: string; sum: string };
  groupSum: number;
}

@Component({
  selector: 'app-sales-report',
  standalone: true,
  imports: [CommonModule, FormsModule, HttpClientModule, EmailFormComponent, SalesReportModalComponent],
  providers: [ExchangeRateService],
  templateUrl: './sales-report.component.html',
  styleUrls: ['./sales-report.component.css'],
  schemas: [NO_ERRORS_SCHEMA]
})
export class SalesReportComponent implements OnInit, OnDestroy {
  private firestore = inject(Firestore);
  private auth = inject(Auth);
  private storage = inject(Storage);
  private pdfService = inject(PdfGeneratorService);
  private accessService = inject(AccessService);
  private companyService = inject(CompanyService);
  private storeService = inject(StoreService);
  private invoiceService = inject(InvoiceService);
  private exchangeRateService = inject(ExchangeRateService);
  private feeRateService = inject(FeeRateService);
  private salesInvoiceService = inject(SalesInvoiceService);
  private cd = inject(ChangeDetectorRef);
  private brandService = inject(BrandService);
  private photoboothService = inject(PhotoboothService);

  // Core State
  periods: string[] = [];
  selectedPeriod: string = '';
  isLoadingGrid: boolean = false;
  gridLoadError = false;
  private gridLoadSeq = 0;
  
  companies: any[] = [];
  companyGridData: any[] = [];
  filteredCompanyGridData: any[] = [];
  
  // Filters
  showCompanyFilterDropdown = false;
  showCountryFilterDropdown = false;
  filterCountry: string = 'All';
  availableCountries: string[] = [];
  filterCompanyIds: Set<string> = new Set();

  dashboardStats = { totalRevenue: 0 };
  selectedBrand: Brand | null = null;
  private brandSubscription?: Subscription;

  private subscriptions: Subscription[] = [];
  private rates: SmrsRate[] = [];
  private exchangeRateCache = new Map<string, { rate: number, date?: Date }>();
  brands: Brand[] = [];

  // Modals
  isReportModalOpen = false;
  selectedCompany: any = null;
  reportData: any = null;

  // Monthly Breakdown State
  isLoadingMonthlyDetails: boolean = false;
  monthlyDetailRows: MonthlyDeviceDetail[] = [];
  filteredMonthlyDetailRows: MonthlyDeviceDetail[] = [];
  availableBranches: string[] = [];
  selectedBranchFilter: string = 'All';

  // Email State
  isEmailModalOpen = false;
  isSavingPDFs = false;
  emailData: any = { to: '', subject: '', body: '', attachments: [] };
  currentEmailRow: any = null;

  // Image Upload State
  isImageUploadModalOpen = false;
  uploadingImage = false;
  imageUploadProgress = 0;
  selectedImageFile: File | null = null;
  currentUploadRow: any = null;
  uploadedImageUrl: string | null = null;
  deletingImageIndex: number | null = null;

  ngOnInit() {
    this.generatePeriods();
    
    // Fetch Companies
    this.subscriptions.push(
      combineLatest([
        this.companyService.getCompanies(),
        this.brandService.getBrands()
      ]).subscribe(async ([companies, brands]) => {
        this.brands = brands;
        let finalCompanies = companies.filter(c => (c as any)['isDeleted'] !== true);
        const user = this.auth.currentUser;
        if (user) {
          const profile = await firstValueFrom(this.accessService.getUserProfile(user.uid));
          const scope = await this.accessService.getUserPageScope('sales-report', profile);
          if (scope.isRestricted) {
            const allowedCompanySet = new Set(scope.companyIds);
            finalCompanies = finalCompanies.filter(c => c.id && allowedCompanySet.has(c.id));
          }
        }
        this.companies = finalCompanies;
        this.initCompanyFilters();
        if (this.selectedPeriod) this.loadGridData();
      }),
      this.feeRateService.getRates().subscribe(rates => {
        this.rates = rates;
        if (this.selectedPeriod) {
          this.loadGridData();
        }
      })
    );

    this.brandSubscription = this.brandService.selectedBrand$.subscribe(brand => {
      const brandChanged = this.selectedBrand?.id !== brand?.id;
      this.selectedBrand = brand;
      if (brandChanged && this.selectedPeriod && this.companies.length > 0) {
        this.loadGridData();
      }
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.brandSubscription) {
      this.brandSubscription.unsubscribe();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler(event: Event) {
    if (this.isImageUploadModalOpen && !this.uploadingImage) {
      this.closeImageUploadModal();
      return;
    }
    if (this.isReportModalOpen) {
      this.closeReportModal();
    }
    if (this.showCompanyFilterDropdown) this.showCompanyFilterDropdown = false;
    if (this.showCountryFilterDropdown) this.showCountryFilterDropdown = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.company-filter-dropdown')) {
      this.showCompanyFilterDropdown = false;
    }
    if (!target.closest('.country-filter-dropdown')) {
      this.showCountryFilterDropdown = false;
    }
  }

  getBrandName(id: string): string {
    const brand = this.brands.find(b => b.id === id);
    return brand ? brand.name : id;
  }

  get activeBrandName(): string {
    return this.selectedBrand?.name || '';
  }

  /** A company or rate belongs to the selected brand when its brand (document id or name) is that brand. With no brand selected nothing is filtered out. */
  isInSelectedBrand(brandRef: string | undefined | null): boolean {
    const selected = this.selectedBrand;
    if (!selected) return true;
    const ref = (brandRef || '').trim();
    return ref === selected.id || this.getBrandName(ref).trim() === (selected.name || '').trim();
  }

  get activeTabCompanies() {
    return this.companies.filter(c => this.isInSelectedBrand(c.brand));
  }

  get activeFilterCompanyIdsCount() {
    return this.activeTabCompanies.filter(c => this.filterCompanyIds.has(c.id)).length;
  }

  get isAllActiveCompaniesSelected() {
    const active = this.activeTabCompanies;
    return active.length > 0 && active.every(c => this.filterCompanyIds.has(c.id));
  }

  get isKcegOwnedSelected() {
    const kceg = this.activeTabCompanies.filter(c => c.kcegOwned);
    const activeSelected = this.activeTabCompanies.filter(c => this.filterCompanyIds.has(c.id));
    return kceg.length > 0 && activeSelected.length === kceg.length && kceg.every(c => this.filterCompanyIds.has(c.id));
  }

  get displayPeriod(): string {
    if (!this.selectedPeriod) return '';
    const parts = this.selectedPeriod.split('/');
    if (parts.length !== 2) return this.selectedPeriod;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);
    const format = (d: Date) => `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
    return `${format(startDate)}-${format(endDate)}`;
  }

  get monthlyDateRange(): { start: string; end: string; display: string } {
    if (!this.selectedPeriod) return { start: '', end: '', display: '' };
    const parts = this.selectedPeriod.split('/');
    if (parts.length !== 2) return { start: '', end: '', display: '' };
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);
    const pad = (n: number) => n.toString().padStart(2, '0');
    const startStr = `${pad(startDate.getMonth() + 1)}.${pad(startDate.getDate())}.${startDate.getFullYear()}`;
    const endStr = `${pad(endDate.getMonth() + 1)}.${pad(endDate.getDate())}.${endDate.getFullYear()}`;
    return {
      start: startStr,
      end: endStr,
      display: `${startStr} ~ ${endStr}`
    };
  }

  get monthlyTotals() {
    let totalPaper = 0;
    let totalSales = 0;
    let totalOverPayment = 0;
    let totalSum = 0;
    let totalGroupSum = 0;

    this.filteredMonthlyDetailRows.forEach(row => {
      totalPaper += (row.paper || 0);
      const cardSales = row.card?.sales || 0;
      const cashSales = row.cash?.sales || 0;
      const couponSales = row.coupon?.sales || 0;
      totalSales += (cardSales + cashSales + couponSales);

      const cardOver = row.card?.overPayment || 0;
      const cashOver = row.cash?.overPayment || 0;
      totalOverPayment += (cardOver + cashOver);

      const cardSum = row.card?.sum || 0;
      const cashSum = row.cash?.sum || 0;
      totalSum += (cardSum + cashSum + couponSales);

      totalGroupSum += (row.groupSum || 0);
    });

    return {
      paper: totalPaper,
      sales: totalSales,
      overPayment: totalOverPayment,
      sum: totalSum,
      groupSum: totalGroupSum
    };
  }

  generatePeriods() {
    const startDate = new Date(2024, 0, 1);
    const currentDate = new Date();
    const tempDate = new Date(startDate);
    while (tempDate <= currentDate) {
      const year = tempDate.getFullYear();
      const month = (tempDate.getMonth() + 1).toString().padStart(2, '0');
      this.periods.push(`${year}/${month}`);
      tempDate.setMonth(tempDate.getMonth() + 1);
    }
    this.periods.reverse();
  }
  
  async initCompanyFilters() {
    const user = this.auth.currentUser;
    if (user) {
      const savedIds = await this.getUserPreferences(user.uid);
      if (savedIds && Array.isArray(savedIds)) {
        const validIds = savedIds.filter(id => this.companies.some(c => c.id === id));
        if (validIds.length > 0) {
           this.filterCompanyIds = new Set(validIds);
        } else {
           if (savedIds.length > 0) {
             this.selectAllCompanies();
           } else {
             this.filterCompanyIds = new Set();
           }
        }
      } else {
        this.selectAllCompanies();
      }
    } else {
      this.selectAllCompanies();
    }
    this.applyFilters();
  }

  async getUserPreferences(uid: string) {
    try {
      const prefDoc = await getDoc(doc(this.firestore, 'users', uid, 'preferences', 'sales_report'));
      if (prefDoc.exists()) {
        return prefDoc.data()?.['companyIds'] || null;
      }
    } catch (e) {
      console.error(e);
    }
    return null;
  }

  async saveUserPreferences(uid: string, preferences: string[]) {
    try {
      await setDoc(doc(this.firestore, 'users', uid, 'preferences', 'sales_report'), { companyIds: preferences }, { merge: true });
    } catch (e) {
      console.error(e);
    }
  }

  async saveCompanyPreferences() {
    const user = this.auth.currentUser;
    if (user) {
      await this.saveUserPreferences(user.uid, Array.from(this.filterCompanyIds));
    }
  }

  onPeriodChange(period: string) {
    this.selectedPeriod = period;
    this.loadGridData();
  }

  selectAllCompanies() {
    this.filterCompanyIds = new Set(this.companies.map(c => c.id));
  }

  toggleCompanyFilterDropdown() {
    this.showCompanyFilterDropdown = !this.showCompanyFilterDropdown;
  }

  toggleCountryFilterDropdown() {
    this.showCountryFilterDropdown = !this.showCountryFilterDropdown;
  }

  selectCountryFilter(country: string) {
    this.filterCountry = country;
    this.showCountryFilterDropdown = false;
    this.applyFilters();
  }

  async toggleCompanyFilterItem(companyId: string) {
    if (this.filterCompanyIds.has(companyId)) {
      this.filterCompanyIds.delete(companyId);
    } else {
      this.filterCompanyIds.add(companyId);
    }
    this.applyFilters();
    await this.saveCompanyPreferences();
  }

  async toggleAllCompanyFilter() {
    const active = this.activeTabCompanies;
    if (this.isAllActiveCompaniesSelected) {
      active.forEach(c => this.filterCompanyIds.delete(c.id));
    } else {
      active.forEach(c => this.filterCompanyIds.add(c.id));
    }
    this.applyFilters();
    await this.saveCompanyPreferences();
  }

  async toggleKcegOwnedFilter() {
    const kceg = this.activeTabCompanies.filter(c => c.kcegOwned);
    if (this.isKcegOwnedSelected) {
      kceg.forEach(c => this.filterCompanyIds.delete(c.id));
    } else {
      this.activeTabCompanies.forEach(c => this.filterCompanyIds.delete(c.id));
      kceg.forEach(c => this.filterCompanyIds.add(c.id));
    }
    this.applyFilters();
    await this.saveCompanyPreferences();
  }

  applyFilters() {
    this.filteredCompanyGridData = this.companyGridData.filter(row => {
      const countryMatch = this.filterCountry === 'All' || row.country === this.filterCountry;
      const idMatch = this.filterCompanyIds.has(row.id);
      const brandMatch = this.isInSelectedBrand(row.rawCompany.brand);
      
      return countryMatch && idMatch && brandMatch;
    });
    this.calculateDashboardStats();
  }

  calculateDashboardStats() {
    let revenue = 0;
    this.filteredCompanyGridData.forEach(row => {
      revenue += (row.totalRevenue || 0);
    });
    this.dashboardStats = { totalRevenue: revenue };
  }

  async getExchangeRate(currency: string, period: string): Promise<{ rate: number, date?: Date }> {
    const key = `${currency}_${period}`;
    if (this.exchangeRateCache.has(key)) {
      return this.exchangeRateCache.get(key)!;
    }

    const rateData = await this.invoiceService.getExchangeRate(currency, period);
    if (rateData !== null) {
      this.exchangeRateCache.set(key, rateData);
      return rateData;
    }

    try {
      if (currency === 'CAD') {
        const apiData = await this.exchangeRateService.getCADtoUSDRate(period);
        if (apiData) {
          const data = { rate: apiData.rate, date: apiData.date };
          this.exchangeRateCache.set(key, data);
          return data;
        }
      }
    } catch (error) {
      console.warn('Error fetching exchange rate:', error);
    }
    const defaultData = { rate: 1 };
    this.exchangeRateCache.set(key, defaultData);
    return defaultData;
  }

  async loadGridData() {
    if (!this.selectedPeriod) {
      this.companyGridData = [];
      this.filteredCompanyGridData = [];
      return;
    }

    // Brand, period, rates and companies can all trigger a load; only the latest one may publish its result.
    const seq = ++this.gridLoadSeq;
    this.isLoadingGrid = true;
    this.gridLoadError = false;
    try {
      const storesSnap = await this.storeService.getStoresSnapshot();
      const stores = storesSnap.docs.map(d => d.data());
      
      const statusQuery = query(collection(this.firestore, 'sales_report_status'), where('period', '==', this.selectedPeriod));
      const statusDocs = await getDocs(statusQuery);
      const statusMap = new Map<string, any>();
      statusDocs.forEach(d => {
        statusMap.set(d.data()['companyId'], d.data());
      });

      const aggDocs = await this.invoiceService.getAggregations(this.selectedPeriod);
      
      const activeTabRates = this.rates.filter((r: any) => this.isInSelectedBrand(r.brand));

      const revenueFeesMap = this.invoiceService.calculateRevenueAndFees(aggDocs, stores, activeTabRates, this.selectedPeriod);

      const companyRevenues = new Map<string, number>();
      revenueFeesMap.forEach((val, key) => {
        const [period, cid] = key.split('_');
        if (period === this.selectedPeriod) {
          companyRevenues.set(cid, (companyRevenues.get(cid) || 0) + val.revenue);
        }
      });

      const companyCurrencyMap = new Map<string, string>();
      const companyCountryMap = new Map<string, string>();
      stores.forEach((s: any) => {
        const country = s['country'] || '미국';
        const currency = s['currency'] || (['Canada', '캐나다'].includes(country) ? 'CAD' : 'USD');
        const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
        cids.forEach((cid: string) => {
            if (currency !== 'USD') companyCurrencyMap.set(cid, currency);
            const current = companyCountryMap.get(cid);
            if (country !== '미국') {
                companyCountryMap.set(cid, country);
            } else if (!current) {
                companyCountryMap.set(cid, '미국');
            }
        });
      });

      const brandCompanies = this.activeTabCompanies;
      const rows = await Promise.all(brandCompanies.map(async c => {
         const totalSales = companyRevenues.get(c.id) || 0;
         const currency = companyCurrencyMap.get(c.id) || 'USD';
         const country = companyCountryMap.get(c.id) || '미국';
         const isInternational = currency !== 'USD';
         let exchangeRate = 1;
         if (isInternational) {
             const rateData = await this.getExchangeRate(currency, this.selectedPeriod);
             exchangeRate = rateData.rate;
         }
         const totalRevUSD = isInternational ? totalSales * exchangeRate : totalSales;
         return {
           id: c.id,
           companyName: c.company,
           address: c.address || '',
           totalRevenue: totalRevUSD,
           country: country,
           rawCompany: c,
           emailStatus: statusMap.get(c.id)?.emailStatus || 'Not Sent',
           imageUrl: statusMap.get(c.id)?.imageUrl || null,
           imageAttachments: statusMap.get(c.id)?.imageAttachments || []
         };
      }));

      if (seq !== this.gridLoadSeq) return; // superseded by a newer load
      this.companyGridData = rows;

      // Countries offered come only from the selected brand's companies (no other brand's data leaks into the filter).
      const countries = new Set<string>();
      this.companyGridData.forEach(r => countries.add(r.country));
      this.availableCountries = Array.from(countries).sort();
      if (this.filterCountry !== 'All' && !countries.has(this.filterCountry)) {
        this.filterCountry = 'All';
      }

      this.applyFilters();
    } catch (error) {
      console.error('Error loading grid data', error);
      if (seq === this.gridLoadSeq) {
        this.gridLoadError = true;
        this.companyGridData = [];
        this.filteredCompanyGridData = [];
        this.calculateDashboardStats();
      }
    } finally {
      if (seq === this.gridLoadSeq) {
        this.isLoadingGrid = false;
      }
    }
  }

  // --- View Modal ---
  async viewReport(row: any) {
    this.selectedCompany = row.rawCompany;
    this.reportData = { ...row };
    this.isReportModalOpen = true;
    await this.loadMonthlyBreakdown(row.rawCompany);
  }

  closeReportModal() {
    this.isReportModalOpen = false;
    this.selectedCompany = null;
    this.reportData = null;
    this.monthlyDetailRows = [];
    this.filteredMonthlyDetailRows = [];
  }

  async loadMonthlyBreakdown(company: any) {
    if (!company) return;
    this.isLoadingMonthlyDetails = true;
    this.monthlyDetailRows = [];
    this.filteredMonthlyDetailRows = [];
    this.availableBranches = [];
    this.selectedBranchFilter = 'All';

    try {
      const parts = this.selectedPeriod.split('/');
      if (parts.length !== 2) return;
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0); // last day
      const pad = (n: number) => n.toString().padStart(2, '0');
      const startDateStr = `${year}-${pad(month)}-01`;
      const endDateStr = `${year}-${pad(month)}-${pad(endDate.getDate())}`;

      const companyId = company.id;
      const companyBrandRef = (company.brand || '').toString().trim();
      const companyBrand = this.brands.find(b => b.id === companyBrandRef || (b.name || '').trim() === companyBrandRef);
      const brandId = companyBrand?.id || '';
      const brandName = companyBrand?.name || '';

      // 1. Get stores for company
      const stores = await this.storeService.getStoresByCompanyId(companyId);
      
      const allStoresSnap = await this.storeService.getStoresSnapshot();
      const allStores: any[] = allStoresSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const companyStores = allStores.filter((s: any) => {
        const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
        return cids.includes(companyId) || s['company'] === company.company;
      });

      // Merge unique stores
      const storeMap = new Map<string, any>();
      stores.forEach(s => storeMap.set(s.storeId || s.id, s));
      companyStores.forEach(s => storeMap.set(s.storeId || s.id, s));

      const companyStoreList = Array.from(storeMap.values());
      const storeDocIds = companyStoreList.map(s => s.id).filter(Boolean);
      const storeCodeIds = companyStoreList.map(s => s.storeId).filter(Boolean);
      const allStoreIdentifiers = new Set([...storeDocIds, ...storeCodeIds].map(s => s.toString().trim().toLowerCase()));

      // 2. Fetch all devices date range revenue using shared PhotoboothService method (same as Daily Report page)
      const allDevicesData = await this.photoboothService.getAllDevicesDateRangeRevenue(startDateStr, endDateStr);

      const branchSet = new Set<string>();
      companyStoreList.forEach(s => {
        const name = s.name || s.storeId || s.id;
        branchSet.add(name);
      });

      const detailRows: MonthlyDeviceDetail[] = [];
      const addedDeviceIds = new Set<string>();

      // Fetch all store devices explicitly to ensure none are missed
      for (const store of companyStoreList) {
        const storeDevices = (await this.storeService.getDevices(store.id || '', store.storeId))
          .filter((d: any) => deviceBelongsToBrand(d, brandId, brandName));
        for (const sDev of storeDevices) {
          const sDevId = sDev.deviceId || sDev.id;
          if (!sDevId || addedDeviceIds.has(sDevId)) continue;
          addedDeviceIds.add(sDevId);

          const matchedDev = allDevicesData.find(d => d.deviceId === sDevId);
          let data = matchedDev?.data || {
            CardTotal: 0,
            CashTotal: 0,
            CouponTotal: 0,
            DailyForcedAmount: 0,
            DailyOverpaidAmount: 0,
            DailyRevenue: 0,
            DailySessions: 0
          };
          const deviceData = { ...sDev, ...(matchedDev?.deviceData || {}) };

          // If daily reports returned 0 for card/cash, check if monthly document exists
          if (!data.CardTotal && !data.CashTotal && !data.CouponTotal) {
            try {
              const monthlyRep = await this.photoboothService.getMonthlyReport(sDevId, this.selectedPeriod.replace('/', '-')) || 
                                 await this.photoboothService.getMonthlyReport(sDevId, this.selectedPeriod);
              if (monthlyRep) {
                data = {
                  CardTotal: Number(monthlyRep['CardTotal'] || monthlyRep['cardTotal'] || monthlyRep['credit'] || 0),
                  CashTotal: Number(monthlyRep['CashTotal'] || monthlyRep['cashTotal'] || monthlyRep['cash'] || 0),
                  CouponTotal: Number(monthlyRep['CouponTotal'] || monthlyRep['couponTotal'] || monthlyRep['coupon'] || 0),
                  DailyForcedAmount: Number(monthlyRep['DailyForcedAmount'] || monthlyRep['forcedTotal'] || monthlyRep['forced'] || 0),
                  DailyOverpaidAmount: Number(monthlyRep['TotalOverpaidAmount'] || monthlyRep['OverpaidAmount'] || monthlyRep['overpaidAmount'] || monthlyRep['overCharge'] || 0),
                  DailyRevenue: Number(monthlyRep['DailyRevenue'] || 0),
                  DailySessions: Number(monthlyRep['DailySessions'] || monthlyRep['dailySessions'] || monthlyRep['totalSession'] || 0)
                };
              }
            } catch (err) {
              console.warn(`Could not fetch fallback monthly report for ${sDevId}:`, err);
            }
          }

          const storeId = store.storeId || store.id;
          const storeName = store.name || store.storeId || store.id;
          const deviceName = deviceData.deviceName || deviceData.name || sDevId;

          const cardSales = Number(data.CardTotal || data.cardTotal || data.credit || 0);
          const cashSales = Number(data.CashTotal || data.cashTotal || data.cash || 0);
          const couponSales = Number(data.CouponTotal || data.couponTotal || data.coupon || 0);
          const forcedSales = Number(data.DailyForcedAmount || data.forcedTotal || data.forced || 0);
          const overPayment = Number(data.DailyOverpaidAmount || data.overpaidAmount || data.overCharge || 0);
          const paper = Number(deviceData.printsLeft ?? deviceData.remainingPapers ?? 0);

          const cardSum = cardSales;
          const cashSum = cashSales;
          const groupSum = cardSales + cashSales;

          detailRows.push({
            storeId: storeId,
            storeName: storeName,
            deviceId: sDevId,
            deviceName: deviceName,
            paper: paper,
            card: { sales: cardSales, overPayment: 0, sum: cardSum },
            cash: { sales: cashSales, overPayment: 0, sum: cashSum },
            forced: { sales: 0, overPayment: '.', sum: '.' },
            coupon: { sales: 0, overPayment: '.', sum: '.' },
            groupSum: groupSum
          });
        }
      }

      // Also include any devices from allDevicesData that matched store identifiers but weren't in getDevices
      const companyDevicesData = allDevicesData.filter((d: any) => {
        const devStoreId = (d.deviceData?.storeId || '').toString().trim().toLowerCase();
        const devStoreDocId = (d.deviceData?.storeDocId || '').toString().trim().toLowerCase();
        return (allStoreIdentifiers.has(devStoreId) || allStoreIdentifiers.has(devStoreDocId))
          && deviceBelongsToBrand(d.deviceData, brandId, brandName);
      });

      for (const dev of companyDevicesData) {
        if (addedDeviceIds.has(dev.deviceId)) continue;
        addedDeviceIds.add(dev.deviceId);

        const data = dev.data || {};
        const deviceId = dev.deviceId;
        const deviceData = dev.deviceData || {};
        const devStore = companyStoreList.find(s => 
          s.id === deviceData.storeDocId || 
          s.storeId === deviceData.storeId || 
          s.id === deviceData.storeId
        );
        const storeId = devStore?.storeId || deviceData.storeId || 'Unknown';
        const storeName = devStore?.name || deviceData.storeName || storeId;
        const deviceName = deviceData.deviceName || deviceData.name || deviceId;

        const cardSales = Number(data.CardTotal || data.cardTotal || data.credit || 0);
        const cashSales = Number(data.CashTotal || data.cashTotal || data.cash || 0);
        const couponSales = Number(data.CouponTotal || data.couponTotal || data.coupon || 0);
        const forcedSales = Number(data.DailyForcedAmount || data.forcedTotal || data.forced || 0);
        const overPayment = Number(data.DailyOverpaidAmount || data.overpaidAmount || data.overCharge || 0);
        const paper = Number(deviceData.printsLeft ?? deviceData.remainingPapers ?? 0);

        const cardSum = cardSales;
        const cashSum = cashSales;
        const groupSum = cardSales + cashSales;

        detailRows.push({
          storeId: storeId,
          storeName: storeName,
          deviceId: deviceId,
          deviceName: deviceName,
          paper: paper,
          card: { sales: cardSales, overPayment: 0, sum: cardSum },
          cash: { sales: cashSales, overPayment: 0, sum: cashSum },
          forced: { sales: 0, overPayment: '.', sum: '.' },
          coupon: { sales: 0, overPayment: '.', sum: '.' },
          groupSum: groupSum
        });
      }

      this.monthlyDetailRows = detailRows;
      this.applyBranchFilter();
    } catch (error) {
      console.error('Error loading monthly breakdown:', error);
    } finally {
      this.isLoadingMonthlyDetails = false;
      this.cd.detectChanges();
    }
  }

  applyBranchFilter() {
    if (!this.selectedBranchFilter || this.selectedBranchFilter === 'All') {
      this.filteredMonthlyDetailRows = [...this.monthlyDetailRows];
    } else {
      const filterLower = this.selectedBranchFilter.toLowerCase().trim();
      this.filteredMonthlyDetailRows = this.monthlyDetailRows.filter(r => 
        r.storeId.toLowerCase().includes(filterLower) || 
        r.storeName.toLowerCase().includes(filterLower)
      );
    }
  }

  exportMonthlyReportExcel() {
    if (!this.filteredMonthlyDetailRows || this.filteredMonthlyDetailRows.length === 0) {
      alert('No report data to export.');
      return;
    }

    const wb = XLSX.utils.book_new();
    const excelData: any[] = [];

    // Title & Info
    excelData.push(['Sales Report - Monthly Detail']);
    excelData.push(['Brand', this.activeBrandName]);
    excelData.push(['Company', this.selectedCompany?.company || '']);
    excelData.push(['Period', this.monthlyDateRange.display]);
    excelData.push([]);

    // Table Header
    excelData.push([
      'StoreID',
      'Device ID',
      'Device Name',
      'Paper',
      'Payment Type',
      'Sales',
      'Over Payment',
      'Sum',
      'Group Sum'
    ]);

    // Data rows
    this.filteredMonthlyDetailRows.forEach(row => {
      excelData.push([
        row.storeId,
        row.deviceId,
        row.deviceName,
        row.paper,
        'Card',
        row.card.sales,
        row.card.overPayment,
        row.card.sum,
        row.groupSum
      ]);
      excelData.push([
        '',
        '',
        '',
        '',
        'Cash',
        row.cash.sales,
        row.cash.overPayment,
        row.cash.sum,
        ''
      ]);
      excelData.push([
        '',
        '',
        '',
        '',
        'Forced',
        row.forced.sales,
        row.forced.overPayment,
        row.forced.sum,
        ''
      ]);
      excelData.push([
        '',
        '',
        '',
        '',
        'Coupon',
        row.coupon.sales,
        row.coupon.overPayment,
        row.coupon.sum,
        ''
      ]);
    });

    // Total row
    const totals = this.monthlyTotals;
    excelData.push([
      'Total Sum',
      '',
      '',
      totals.paper,
      '',
      totals.sales,
      totals.overPayment,
      totals.sum,
      totals.groupSum
    ]);

    const ws = XLSX.utils.aoa_to_sheet(excelData);
    XLSX.utils.book_append_sheet(wb, ws, 'Sales Report');

    const filename = `Sales_Report_${(this.selectedCompany?.company || 'Monthly').replace(/\s+/g, '_')}_${this.selectedPeriod.replace('/', '-')}.xlsx`;
    XLSX.writeFile(wb, filename);
  }

  // --- PDF & Print logic ---
  async printReport() {
    if (!this.selectedCompany) return;
    await this.handlePdfAction(this.selectedCompany, 'open');
  }

  async saveReportPdf() {
    if (!this.selectedCompany) return;
    await this.handlePdfAction(this.selectedCompany, 'save');
  }

  private async handlePdfAction(company: any, action: 'save' | 'open' | 'blob'): Promise<Blob | void> {
    let pdfWindow: Window | null = null;
    if (action === 'open') {
      pdfWindow = window.open('', '_blank');
      if (pdfWindow) {
        pdfWindow.document.write('<html><body><div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;"><div>Generating PDF... Please wait.</div></div></body></html>');
      }
    }

    this.cd.detectChanges();
    await new Promise(resolve => setTimeout(resolve, 1000));

    const content = document.getElementById('sales-pdf-content');
    if (content) {
      const filename = `Sales_Report_${company.company.replace(/\s+/g, '_')}_${this.selectedPeriod.replace('/', '-')}.pdf`;
      try {
        if (action === 'save') {
          await this.pdfService.savePdf(content, filename, 'portrait');
        } else if (action === 'open') {
          await this.pdfService.openPdf(content, filename, 'portrait', pdfWindow || undefined);
        } else if (action === 'blob') {
          return await this.pdfService.generateBlob(content, filename, 'portrait');
        }
      } catch (error) {
        if (pdfWindow) pdfWindow.close();
        console.error(error);
        if (action === 'blob') throw error;
      }
    } else {
      if (pdfWindow) pdfWindow.close();
      if (action === 'blob') throw new Error('PDF content not found');
    }
  }

  // --- Email Logic ---
  async emailSingleReport(row: any) {
    this.currentEmailRow = row;
    this.isSavingPDFs = true;
    try {
      this.selectedCompany = row.rawCompany;
      this.reportData = { ...row };
      await this.loadMonthlyBreakdown(row.rawCompany);

      const pdfBlob = await this.handlePdfAction(row.rawCompany, 'blob') as Blob;
      const user = this.auth.currentUser;
      
      const periodStr = this.selectedPeriod.replace(/\//g, '-');
      const fileRefName = `Sales_Report_${row.rawCompany.company.replace(/\s+/g, '_')}_${periodStr}`;
      
      const { downloadURL, storagePath } = await this.salesInvoiceService.uploadTempPdf(user?.uid || 'temp', fileRefName, pdfBlob);

      let body = `Dear ${row.rawCompany.pocName || 'Partner'},\n\nPlease find attached the sales report for ${this.displayPeriod}.\n\nBest regards,\nLFC Franchise Inc.`;

      if (user) {
        try {
          const template = await this.accessService.getEmailTemplate(user.uid, 'sales_report');
          if (template) body = template;
        } catch (e) {}
      }

      this.emailData = {
        to: row.rawCompany.mallEmail || row.rawCompany.email || '',
        subject: `Sales Report ${this.displayPeriod} - ${row.rawCompany.company}`,
        body: body,
        attachments: [{
          name: `${fileRefName}.pdf`,
          url: downloadURL,
          storagePath: storagePath
        }]
      };
      this.isEmailModalOpen = true;
    } catch (error) {
      console.error('Error generating email attachment:', error);
      alert('Failed to generate report attachment.');
    } finally {
      this.isSavingPDFs = false;
    }
  }

  async onEmailSent(event?: any) {
    if (this.currentEmailRow) {
      const row = this.currentEmailRow;
      try {
        const docId = `${this.selectedPeriod.replace('/', '-')}_${row.id}`;
        await setDoc(doc(this.firestore, 'sales_report_status', docId), {
          period: this.selectedPeriod,
          companyId: row.id,
          emailStatus: 'Sent',
          emailSentAt: new Date().toISOString(),
          recipients: this.emailData.to || ''
        }, { merge: true });
        
        row.emailStatus = 'Sent';
        this.cd.detectChanges();
      } catch (e) {
        console.error("Failed to update email status", e);
      }
    }
    this.closeEmailModal(false);
  }

  async closeEmailModal(shouldDelete: boolean = false) {
    if (shouldDelete && this.emailData.attachments && this.emailData.attachments.length > 0) {
      for (const attachment of this.emailData.attachments) {
        if (attachment.storagePath) {
          try {
            await this.salesInvoiceService.deleteTempPdf(attachment.storagePath);
          } catch (e) {}
        }
      }
    }
    this.isEmailModalOpen = false;
    this.emailData = { to: '', subject: '', body: '', attachments: [] };
    this.currentEmailRow = null;
  }

  // --- Image Upload Logic ---
  openImageUploadModal(row: any) {
    this.currentUploadRow = row;
    this.selectedImageFile = null;
    this.uploadedImageUrl = null;
    this.deletingImageIndex = null;
    this.imageUploadProgress = 0;
    this.uploadingImage = false;
    this.isImageUploadModalOpen = true;
  }

  closeImageUploadModal() {
    if (this.uploadingImage) return;
    this.isImageUploadModalOpen = false;
    this.currentUploadRow = null;
    this.selectedImageFile = null;
  }

  onImageFileSelected(event: any) {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      this.selectedImageFile = file;
    } else {
      alert('Please select a valid image file.');
      this.selectedImageFile = null;
      event.target.value = ''; // Reset input
    }
  }

  async uploadImage() {
    if (!this.selectedImageFile || !this.currentUploadRow) return;

    this.uploadingImage = true;
    this.imageUploadProgress = 0;
    this.uploadedImageUrl = null;
    
    const fileName = this.selectedImageFile.name;

    try {
      const periodStr = this.selectedPeriod.replace(/\//g, '-');
      const companyName = this.currentUploadRow.companyName.replace(/\s+/g, '_');
      const timestamp = Date.now();
      const extension = fileName.split('.').pop() || 'png';
      
      const user = this.auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      
      const filePath = `sales_report_attachments/${companyName}_${periodStr}_${timestamp}.${extension}`;
      const storageRef = ref(this.storage, filePath);

      const uploadTask = uploadBytesResumable(storageRef, this.selectedImageFile);

      uploadTask.on('state_changed', 
        (snapshot) => {
          this.imageUploadProgress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          this.cd.detectChanges();
        }, 
        (error) => {
          console.error('Upload failed:', error);
          alert('Image upload failed.');
          this.uploadingImage = false;
          this.cd.detectChanges();
        }, 
         async () => {
          this.uploadedImageUrl = await getDownloadURL(uploadTask.snapshot.ref);
          
          const newAttachment = {
            url: this.uploadedImageUrl,
            storagePath: filePath,
            name: fileName
          };

          const docId = `${this.selectedPeriod.replace(/\//g, '-')}_${this.currentUploadRow.id}`;
          let currentAttachments = this.currentUploadRow.imageAttachments || [];
          currentAttachments.push(newAttachment);

          await setDoc(doc(this.firestore, 'sales_report_status', docId), {
            period: this.selectedPeriod,
            companyId: this.currentUploadRow.id,
            imageAttachments: currentAttachments,
            updatedAt: new Date().toISOString()
          }, { merge: true });

          this.currentUploadRow.imageAttachments = currentAttachments;
          this.uploadingImage = false;
          this.selectedImageFile = null;
          
          const input = document.getElementById('imageUploadInput') as HTMLInputElement;
          if (input) input.value = '';
          
          this.cd.detectChanges();
        }
      );
    } catch (error) {
      console.error('Error starting upload:', error);
      this.uploadingImage = false;
      alert('An error occurred starting the upload.');
      this.cd.detectChanges();
    }
  }

  async deleteImage(index: number) {
    if (!this.currentUploadRow || !this.currentUploadRow.imageAttachments) return;
    if (!confirm('Are you sure you want to delete this attached image?')) return;
    
    this.deletingImageIndex = index;
    const attachment = this.currentUploadRow.imageAttachments[index];
    
    try {
      if (attachment.storagePath) {
        const storageRef = ref(this.storage, attachment.storagePath);
        await deleteObject(storageRef);
      }
      
      this.currentUploadRow.imageAttachments.splice(index, 1);
      const docId = `${this.selectedPeriod.replace(/\//g, '-')}_${this.currentUploadRow.id}`;
      await setDoc(doc(this.firestore, 'sales_report_status', docId), {
        imageAttachments: this.currentUploadRow.imageAttachments
      }, { merge: true });
      
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image.');
    } finally {
      this.deletingImageIndex = null;
      this.cd.detectChanges();
    }
  }
  
  async deleteLegacyImage() {
    if (!this.currentUploadRow) return;
    if (!confirm('Are you sure you want to remove this image?')) return;
    try {
      const docId = `${this.selectedPeriod.replace(/\//g, '-')}_${this.currentUploadRow.id}`;
      await setDoc(doc(this.firestore, 'sales_report_status', docId), { imageUrl: null }, { merge: true });
      this.currentUploadRow.imageUrl = null;
      this.cd.detectChanges();
    } catch (error) {
      console.error('Error deleting legacy image:', error);
    }
  }
}
